// Money helpers (ticket 125). Stored as a plain number (float, USD, cents
// kept, negatives allowed); rendered as '$389,000' / '$1,234.50' / '-$20,000'.
// The input accepts whatever people type or paste ('$1,234.50', '1234.5',
// '-500', '(500)') and re-formats it as a live mask.

// Prop value → number | null. Accepts number, numeric string ('425000', '$4,250.50',
// '-1,000'), '', null, undefined. Text with no digits → null.
export function toNumber(v) {
  if (v === '' || v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  return parseMoneyText(String(v));
}

// What the user typed → { neg, int, frac }. int is digits with leading zeros
// dropped; frac is '' | '.' | '.5' | '.50' (at most two cents digits).
// A leading '-' or accounting parentheses '(1,234)' mark the amount negative.
export function splitMoneyText(text) {
  const s = String(text ?? '');
  const cleaned = s.replace(/[^0-9.-]/g, '');
  const neg = cleaned.startsWith('-') || /^\s*\(.*\)\s*$/.test(s);
  const unsigned = cleaned.replace(/-/g, '');
  const dot = unsigned.indexOf('.');
  const intRaw = (dot === -1 ? unsigned : unsigned.slice(0, dot)).replace(/^0+(?=\d)/, '');
  const frac = dot === -1 ? '' : '.' + unsigned.slice(dot + 1).replace(/\./g, '').slice(0, 2);
  return { neg, int: intRaw, frac };
}

// Typed text → number | null. '', '$', '.', '-', 'abc' → null. '12.' → 12, '.5' → 0.5,
// '-1,250.75' → -1250.75.
export function parseMoneyText(text) {
  const { neg, int, frac } = splitMoneyText(text);
  if (!int && frac.replace('.', '') === '') return null;
  const n = Number((int || '0') + frac);
  if (!Number.isFinite(n)) return null;
  return neg && n !== 0 ? -n : n;
}

// Typed text → display while focused: sign + '$' + grouped int + frac exactly as
// typed ('$12.' stays '$12.'; '-' alone stays '-' so the user can keep typing).
export function formatMoneyInput(text) {
  const { neg, int, frac } = splitMoneyText(text);
  if (!int && !frac) return neg ? '-' : '';
  const grouped = (int || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${neg ? '-' : ''}$${grouped}${frac}`;
}

// number|null → settled display. Cents are shown when present (1234.5 → '$1,234.50');
// whole amounts stay whole (389000 → '$389,000') unless `cents` forces two decimals.
export function formatMoney(n, cents = false) {
  const v = toNumber(n);
  if (v === null) return '';
  const hasCents = Math.round(Math.abs(v) * 100) % 100 !== 0;
  const digits = cents || hasCents ? 2 : 0;
  const body = Math.abs(v).toLocaleString('en-US', {
    minimumFractionDigits: digits, maximumFractionDigits: 2,
  });
  return `${v < 0 ? '-' : ''}$${body}`;
}
